import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';

/**
 * WebSocket hook with REST fallback for environments where WebSocket is not available.
 * In production, WebSocket may not be routed through the ingress, so we fallback to polling.
 */
export function useWebSocket(sessionId) {
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [useRestFallback, setUseRestFallback] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  // Try WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!sessionId) return;
    
    // Build WebSocket URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl
      ?.replace('https://', 'wss://')
      .replace('http://', 'ws://');
    
    if (!wsUrl) {
      console.log('No WebSocket URL, using REST fallback');
      setUseRestFallback(true);
      setConnected(true);
      return;
    }

    try {
      const ws = new WebSocket(`${wsUrl}/ws/${sessionId}`);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'pong') return;
        
        if (data.type === 'response') {
          setLoading(false);
          setMessages(prev => [...prev, {
            id: data.response_id,
            role: 'assistant',
            content: data.content,
            thinking: data.thinking,
            tier: data.tier_used,
            latency_ms: data.latency_ms,
            tokens_input: data.tokens_input,
            tokens_output: data.tokens_output,
            cache_hit: data.cache_hit,
            cost_estimate: data.cost_estimate,
            timestamp: data.timestamp,
          }]);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);
        
        // After 3 failed attempts, fallback to REST
        reconnectAttempts.current++;
        if (reconnectAttempts.current >= 3) {
          console.log('WebSocket unavailable, using REST fallback');
          setUseRestFallback(true);
          setConnected(true);
          return;
        }
        
        // Try to reconnect
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 2000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      setUseRestFallback(true);
      setConnected(true);
    }
  }, [sessionId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(async (content, enableThinking = false, forceTier = null) => {
    // Add user message to state
    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Use REST fallback or WebSocket
    if (useRestFallback || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // REST API call
      try {
        const response = await api.chat(
          content,
          sessionId,
          enableThinking,
          forceTier === 'auto' ? null : forceTier
        );

        setMessages(prev => [...prev, {
          id: response.data.id,
          role: 'assistant',
          content: response.data.response,
          thinking: response.data.thinking,
          tier: response.data.tier_used,
          latency_ms: response.data.latency_ms,
          tokens_input: response.data.tokens_input,
          tokens_output: response.data.tokens_output,
          cache_hit: response.data.cache_hit,
          cost_estimate: response.data.cost_estimate,
          timestamp: response.data.timestamp,
        }]);
      } catch (err) {
        console.error('Chat error:', err);
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          error: true,
          timestamp: new Date().toISOString(),
        }]);
      } finally {
        setLoading(false);
      }
      return true;
    }

    // WebSocket send
    wsRef.current.send(JSON.stringify({
      type: 'message',
      content,
      enable_thinking: enableThinking,
      force_tier: forceTier === 'auto' ? null : forceTier,
    }));

    return true;
  }, [sessionId, useRestFallback]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Connect on mount - start with REST (more reliable)
  useEffect(() => {
    // For now, use REST fallback by default for reliability
    // WebSocket can be enabled when proper proxy routing is configured
    setUseRestFallback(true);
    setConnected(true);
    
    // Optionally try WebSocket in background
    // connectWebSocket();
    
    return disconnect;
  }, [disconnect]);

  return {
    connected,
    messages,
    loading,
    sendMessage,
    clearMessages,
    reconnect: connectWebSocket,
    isWebSocket: !useRestFallback,
  };
}

export default useWebSocket;
